import { createSelector } from 'reselect';
import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';
import { publish } from 'services/CentrifugoWS';

import { ComponentHeader } from './ComponentHeader';
import { ComponentConfiguration } from './ComponentConfiguration';
import {
  completePurchaseCentrifugoMessage,
  customerCreationAcceptedCentrifugoMessage,
  finishedSaleCentrifugoMessage,
  pluginID,
  customerCreationRejectionMessage,
  updateCustomerDeclinedMessage,
  updateCustomerSuccessMessage,
} from './constants';
import DuplicateCustomersSelectionForm from './DuplicateCustomersSelectionForm';
import customerDisplayReducer, {
  getChannelName,
  getAfterSaleAction,
  getQRCodeConfig,
  getChannel,
} from './rdx/reducer';
import { sendUpsellProductsToDisplay } from './features/upsellProducts';
import { UIProductBasePriceInProductOrderEdit } from './features/tipProduct';
import { CustomerViewIcons } from './components';

const customerDisplayPlugin: PosPlugin = {
  id: pluginID,
  name: 'Customer display',
  ComponentConfigurationByLevel: {
    Pos: ComponentConfiguration,
  },
  getStatus: createSelector(getChannelName, channelName => {
    if (!channelName) {
      return {
        type: 'error',
        message: 'Channel not configured',
      };
    }
    return {
      type: 'valid',
      message: `Channel name: ${channelName}`,
    };
  }),
  combineConfiguration: (company = {}, warehouse = {}, pos = {}, user = {}) =>
    R.reduce(R.mergeDeepRight, {}, [company, warehouse, pos, user]),
  onSaveSalesDocument: {
    after: (p, ep) => async (dispatch, getState) => {
      const afterSaleAction = getAfterSaleAction(getState());
      const channelName = getChannelName(getState());
      const QRCodeConfig = getQRCodeConfig(getState());
      if (afterSaleAction === 'email') {
        publish({
          channel: `customer_display:${channelName}`,
          message: completePurchaseCentrifugoMessage,
        });
      } else if (afterSaleAction === 'customQR') {
        publish({
          channel: `customer_display:${channelName}`,
          message: {
            action: 'SHOW_QR',
            qrCodeData: QRCodeConfig.qrCodeData,
            qrTitle: QRCodeConfig.qrTitle,
            qrSubtitle: QRCodeConfig.qrSubtitle,
            isAfterSaleQR: true,
          },
        });
      } else if (afterSaleAction === 'receiptQR') {
        publish({
          channel: `customer_display:${channelName}`,
          message: {
            action: 'SHOW_QR',
            qrCodeData: ep.salesDocument.receiptLink,
            qrTitle: QRCodeConfig.qrTitle,
            qrSubtitle: QRCodeConfig.qrSubtitle,
            isAfterSaleQR: true,
          },
        });
      } else {
        publish({
          channel: `customer_display:${channelName}`,
          message: finishedSaleCentrifugoMessage,
        });
      }
    },
  },
  onSaveCustomer: {
    after: p => async (dispatch, getState) => {
      const channel = getChannel(getState());
      if (!channel) {
        return;
      }
      const wasUpdatingCustomer = p.customerID;
      if (wasUpdatingCustomer) {
        publish({
          channel,
          message: updateCustomerSuccessMessage,
        });
      } else {
        publish({
          channel,
          message: customerCreationAcceptedCentrifugoMessage,
        });
      }
    },
  },
  components: {
    DuplicateCustomersSelectionForm,
  },
  onSoftLogout: {
    before: () => async (_dispatch, getState) => {
      const channelName = getChannelName(getState());
      const channel = `customer_display:${channelName}`;
      publish({
        channel,
        message: finishedSaleCentrifugoMessage,
      });
    },
  },
  onHardLogout: {
    before: () => async (_dispatch, getState) => {
      const channelName = getChannelName(getState());
      const channel = `customer_display:${channelName}`;
      publish({
        channel,
        message: finishedSaleCentrifugoMessage,
      });
    },
  },
  onAddProduct: {
    on: sendUpsellProductsToDisplay,
  },
  ComponentHeader,
  UIProductBasePriceInProductOrderEdit,
  UICustomerViewIcons: CustomerViewIcons,
  reduxReducer: customerDisplayReducer,
};
export default customerDisplayPlugin;
